import { Button, Classes, Dialog, Divider, FormGroup, DialogProps, Intent } from '@blueprintjs/core'
import { DateRange, DateRangeInput } from '@blueprintjs/datetime'
import * as React from 'react'
import client from '../../client'
import ReportHistory from '../../routes/reports/components/ReportHistory'
import { ReportType } from '../../routes/reports/types'
import { showSuccess } from '../../Toast'
import { buildDateOnlyISOString } from '../../utils/date'
import { dateRangeDayCounter, localEndOfDay, localStartOfDay } from '../../utils/dateRange'
import EmailSelector from '../EmailSelector'
import Errors from '../Errors'
import { LoadableSelect } from '../Inputs'

const styles = {
  userSelect: {
    margin: 0,
    textAlign: 'left'
  },
  emailInput: {
    marginBottom: 10
  },
  divider: {
    margin: '0px 0 10px'
  }
}
interface EventLogExporterDialogProps extends DialogProps {
  isOpen: boolean
}

interface ReportGeneratorAPIPayload {
  emails: string[]
  parameters: any
  reportType: ReportType
}

const EventLogExporterDialog: React.FC<EventLogExporterDialogProps> = props => {
  const [emails, setEmails] = React.useState([])
  const [errors, setErrors] = React.useState(null)
  const [userId, setUserId] = React.useState(null)
  const [loading, setLoading] = React.useState(false)

  const timeStart = localStartOfDay()
  const timeEnd = localEndOfDay()

  const [reportDateRange, setReportDateRange] = React.useState<DateRange | null>([timeStart, timeEnd])

  const maxDayCount = 180
  const startKey = 'timeStart'
  const endKey = 'timeEnd'

  const onEmailChange = values => {
    setEmails(values)
    setErrors(null)
  }

  const cleanupAndClose = e => {
    setEmails([])
    setErrors(null)
    setReportDateRange([timeStart, timeEnd])
    props.onClose(e)
  }

  const isDateRangeSet = reportDateRange.filter(date => date).length === 2

  const onSubmit = async () => {
    setLoading(true)
    if (reportDateRange.filter(date => date).length !== 2) {
      setErrors({ errors: { dates: ['both a start and end date must be specified'] } })
      setLoading(false)
      return
    }

    const params = {
      [startKey]: buildDateOnlyISOString(reportDateRange[0]),
      [endKey]: buildDateOnlyISOString(reportDateRange[1]),
      userId
    }

    const payload: ReportGeneratorAPIPayload = {
      emails,
      parameters: params,
      reportType: ReportType.EventLogs
    }

    try {
      await client.generateReport({ asyncReport: payload })
      setErrors(null)
      showSuccess('Report generated')
    } catch (e) {
      setErrors(e.response.data)
    }

    analytics.track(`Event Logs Page Data Export`, {
      startDate: reportDateRange[0],
      endDate: reportDateRange[1],
      dayCount: dateRangeDayCounter(reportDateRange[0], reportDateRange[1]),
      emailCount: emails.length
    })
    setLoading(false)
  }

  const now = new Date()
  const minDate = new Date(now.valueOf())
  minDate.setDate(minDate.getDate() - maxDayCount)

  return (
    <Dialog
      {...props}
      onClose={cleanupAndClose}
      title="Export Event Logs"
      portalClassName="upright-popover-safe-dialog"
    >
      <div className={Classes.DIALOG_BODY}>
        <Errors error={errors} />

        <FormGroup label="Between dates">
          <DateRangeInput
            allowSingleDayRange
            shortcuts={false}
            maxDate={now}
            minDate={minDate}
            formatDate={date => date.toLocaleDateString()}
            onChange={setReportDateRange}
            parseDate={str => new Date(str)}
            value={reportDateRange}
          />
        </FormGroup>

        <FormGroup label="User" labelInfo="(optional)">
          <LoadableSelect
            // title="User (optional)"
            load={() => client.users({ showAll: 1 })}
            process={d => d.users.map(u => ({ value: u.id, label: `${u.username} ${u.active ? '' : '(inactive)'}` }))}
            onChange={v => setUserId(v)}
            value={userId}
            className="users-filter"
            style={styles.userSelect}
            autoload={userId}
            clearable
          />
        </FormGroup>
        <FormGroup
          helperText="We will email you when the report is ready."
          label="Email addresses"
          labelInfo="(optional)"
          style={styles.emailInput}
        >
          <EmailSelector onChange={(values: string[]) => onEmailChange(values)} values={emails} />
        </FormGroup>

        <ReportHistory reportType={ReportType.EventLogs} reportsCount={5} />
      </div>

      <Divider style={styles.divider} />

      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={e => cleanupAndClose(e)}>Cancel</Button>
          <Button
            intent={Intent.PRIMARY}
            onClick={onSubmit}
            loading={loading}
            disabled={errors != null || !isDateRangeSet}
          >
            Generate report
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

export default EventLogExporterDialog
